import { Component, Input, OnChanges, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { format, isSameDay, isSameMonth, isSameYear } from "date-fns";
import { saveAs } from "file-saver-es";
import { Subject } from "rxjs";
import { QueryHistoricExportXlxsRequest } from "src/app/shared/jsonrpc/request/queryHistoricExportXlxs";
import { Base64PayloadResponse } from "src/app/shared/jsonrpc/response/base64PayloadResponse";
import { QueryHistoricTimeseriesEnergyPerPeriodResponse } from "src/app/shared/jsonrpc/response/queryHistoricTimeseriesEnergyPerPeriodResponse";
import { DefaultTypes } from "src/app/shared/type/defaulttypes";
import { ChannelAddress, EdgeConfig, Service, Websocket } from "../../../shared/shared";
import { AbstractHistoryChart } from "../abstracthistorychart";

@Component({
  selector: "metersenergy",
  templateUrl: "./energy.component.html",
  standalone: false,
})
export class EnergyComponent
  extends AbstractHistoryChart implements OnInit, OnChanges, OnDestroy {
  private static readonly EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  private static readonly EXCEL_EXTENSION = ".xlsx";

  @Input() public period: DefaultTypes.HistoryPeriod;
  @Input() public meters: EdgeConfig.Component[] = [];
  public meterID: string;
  public meterIds: string[] = [];
  public consumptionDatasets = [];
  public productionDatasets = [];
  public skeletonHeaderWidths = ["80px", "100px", "100px"];
  public skeletonItemWidths = ["60%", "40px", "40px"];
  public fakeItems = new Array(5);
  private config: EdgeConfig | null = null;
  private stopOnDestroy: Subject<void> = new Subject<void>();

  constructor(
    protected override service: Service,
    protected override translate: TranslateService,
    private route: ActivatedRoute,
    public modalCtrl: ModalController,
    private websocket: Websocket,
  ) {
    super("energy-chart", service, translate);
  }

  public checkIfToday(): boolean {
    const dateYesterday = this.service.historyPeriod.value.to.getDate();
    const dateToday = new Date().getDate();
    return dateYesterday === dateToday;
  }

  // Export meter consumption to csv file
  public exportToMeterCsv() {
    this.startSpinner();
    this.service.getCurrentEdge().then((edge) => {
      this.service.getConfig().then((config) => {
        this.getEnergyChannelAddresses(config).then((channelAddresses) => {
          const request = new QueryHistoricExportXlxsRequest(
            this.service.historyPeriod.value.from,
            this.service.historyPeriod.value.to,
            channelAddresses,
          );
          edge
            .sendRequest(this.websocket, request)
            .then((response) => {
              const r = response as Base64PayloadResponse;
              const binaryString = window.atob(r.result.payload.replace(/\s/g, ""));
              const len = binaryString.length;
              const bytes = new Uint8Array(len);
              for (let i = 0; i < len; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              const data: Blob = new Blob([bytes], {
                type: EnergyComponent.EXCEL_TYPE,
              });
              let fileName = "Export-" + edge.id + "-";
              const dateFrom = this.service.historyPeriod.value.from;
              const dateTo = this.service.historyPeriod.value.to;
              if (isSameDay(dateFrom, dateTo)) {
                fileName += format(dateFrom, "yyyy-MM-dd");
              } else if (isSameMonth(dateFrom, dateTo)) {
                fileName += format(dateFrom, "yyyy-MM-dd") + "-" + format(dateTo, "yyyy-MM-dd");
              } else if (isSameYear(dateFrom, dateTo)) {
                fileName += format(dateFrom, "yyyy-MM-dd") + "-" + format(dateTo, "yyyy-MM-dd");
              } else {
                fileName += format(dateFrom, "yyyy-MM-dd") + "-" + format(dateTo, "yyyy-MM-dd");
              }
              fileName += EnergyComponent.EXCEL_EXTENSION;
              saveAs(data, fileName);
            })
            .catch((reason) => {
              console.warn(reason);
            });
        });
      });
      this.stopSpinner();
    })
      .catch((reason) => {
        console.warn(reason);
        this.stopSpinner();
      });

  }

  public getConsumptionData(index: number): number {
    const data = (this.consumptionDatasets[index]?.data as number[])?.filter((d) => d !== 0);
    return data?.length > 0 ? data[data.length - 1] : 0;
  }
  public getProductionData(index: number): number {
    const data = (this.productionDatasets[index]?.data as number[])?.filter((d) => d !== 0);
    return data?.length > 0 ? data[data.length - 1] : 0;
  }

  ngOnInit() {
    this.service.setCurrentComponent("", this.route);
    this.startSpinner();
    this.meters.forEach((meter) => {
      this.meterIds.push(meter.id);
    });
  }

  ngOnChanges() {
    this.updateChart();
  }

  ngOnDestroy() {
    this.stopOnDestroy.next();
    this.stopOnDestroy.complete();
    this.unsubscribeChartRefresh();
  }


  protected updateChart() {
    this.service.getCurrentEdge().then((edge) => {
      this.service.getConfig().then((config) => {
        this.config = config;
        this.edge = edge;
        this.loadListChart(config);
      })
        .catch((reason) => {
          this.initializeChart();
          return;
        });
    })
      .catch((reason) => {
        this.initializeChart();
        return;
      });
  }
  private loadListChart(config: EdgeConfig) {
    this.getEnergyChannelAddresses(config).then((channelAddresses) => {
      this.queryHistoricTimeseriesDataMeter(
        this.period.from,
        this.period.to,
        channelAddresses,
      ).then((response) => {
        const result = (
          response as QueryHistoricTimeseriesEnergyPerPeriodResponse
        ).result;
        this.consumptionDatasets = [];
        this.productionDatasets = [];
        this.meterIds?.forEach((meterId) => {
          if (`${meterId}/ActiveConsumptionEnergy` in result.data) {
            const consumptionData = result?.data[`${meterId}/ActiveConsumptionEnergy`].map((value) => {
              return value != null ? value / 1000 : 0; // convert to kWh
            }) || [];
            this.consumptionDatasets.push({ data: consumptionData ?? [] });
          }
          if (result && result.data && `${meterId}/ActiveProductionEnergy` in result.data) {
            const productionData = result?.data[`${meterId}/ActiveProductionEnergy`]?.map((value) => {
              return value != null ? value / 1000 : 0;
            }) || [];
            this.productionDatasets.push({ data: productionData ?? [] });
          }
        });

        this.loading = false;
        this.stopSpinner();
      }).catch((reason) => {
        this.consumptionDatasets = [];
        this.productionDatasets = [];
        this.loading = false;
        this.stopSpinner();
      });
    }).catch((reason) => {
      console.warn("Failed to get Energy-Channel-Addresses. Setting loading to false.", reason);
      this.loading = false;
      this.stopSpinner();
    });
  }

  private getEnergyChannelAddresses(
    config: EdgeConfig,
  ): Promise<ChannelAddress[]> {
    return new Promise((resolve) => {
      const result: ChannelAddress[] = [];
      this.meterIds.forEach((meterId) => {
        result.push(new ChannelAddress(meterId, "ActiveConsumptionEnergy"));
        result.push(new ChannelAddress(meterId, "ActiveProductionEnergy"));
      });
      resolve(result);
    });
  }

}
